// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
  type GestureResponderEvent,
  type ImageStyle,
  type LayoutChangeEvent,
  type StyleProp,
} from 'react-native';

import Svg, { Polygon, Polyline, Rect } from 'react-native-svg';

import { Ionicons } from '@expo/vector-icons';
import { ResizeMode, Video } from 'expo-av';

import { useUser } from '@/app/user-context';
// The game-info video solved the same two problems this player has — fitting a box to a
// video's real shape, and reading that shape back on both native and web — so the geometry
// is shared rather than written a second time here.
import {
  readVideoAspectRatio,
  resolveInfoVideoBox,
} from '@/components/roundGameInfoVideoLayout';
import { VideoScrubBar, useVideoScrub } from '@/components/VideoScrubBar';
import { CourseHoleMapModal } from '@/features/course-trace/CourseHoleMapModal';
import { reportHoleInaccurate } from '@/features/course-trace/useCourseAnalysis';
import {
  requestCourseAnalysis,
  useCourseAnalysisStatus,
  useHoleBasemapImage,
} from '@/features/course-trace/useCourseAnalysisStatus';
import { readFlyoverDistance } from '@/features/tour-preview/flyoverDistanceReading';
import { useFlyoverVideos } from '@/features/tour-preview/useFlyoverVideos';
import { formatElevation, formatHoleDistance } from '@/features/units/distance';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import { useDistanceUnit } from '@/stores/distanceUnitState';
import { useHoleBasemap, useHoleBasemapState } from '@/stores/holeBasemapState';
import appColors, { alpha } from '@/theme/colors';

import {
  applyViewTransform,
  buildTileUrl,
  computeHoleViewLayout,
  distanceMeters,
  distanceToRingMeters,
  distanceToSegmentMeters,
  IDENTITY_VIEW,
  MAX_VIEW_ZOOM,
  metersToWorldFraction,
  MIN_VIEW_ZOOM,
  layoutToViewportPoint,
  pointBesideRing,
  projectToLayout,
  unprojectFromLayout,
  viewportToLayoutPoint,
  type LatLon,
  type ViewTransform,
} from './tileMath';
import { BASEMAPS, type HoleBasemap } from './basemap';
import { heightDifference, type HoleElevationProfile } from './elevation';
import {
  DRAWING_FILLS,
  HAZARD_KINDS,
  OVERLAY_COLORS,
  PATH_KINDS,
  ringCentroid,
  ROUGH_FILL,
  type HoleLine,
  type HoleShape,
} from './holeShapes';
import {
  pressPointInElement,
  type MeasurableElement,
  type PressPointEvent,
} from './pressPoint';
import { getTileUri } from './tileCache';
import {
  getSatelliteAttribution,
  getSatelliteTileUrlTemplate,
  LANTMATERIET_ATTRIBUTION,
} from './tileSource';
import type { TeePoint } from './useHoleViewGeometry';
import type { PlayerPosition, PlayerPositionStatus } from './usePlayerPosition';
import { bearingDegrees, formatWindSpeed, relativeWind, type Wind } from './wind';

/**
 * How far off the tee-to-green line still counts as "on this hole".
 *
 * Wide enough for any fairway plus the rough either side, narrow enough that standing on the
 * neighbouring hole does not put a marker on this one. Measured to the line segment, so it
 * also rules out being well past the green.
 */
export const ON_HOLE_RADIUS_METERS = 100;

/** One press of the zoom buttons. Roughly a third closer, which reads as a step. */
const ZOOM_STEP = 1.6;

const clampZoom = (zoom: number) => Math.min(MAX_VIEW_ZOOM, Math.max(MIN_VIEW_ZOOM, zoom));

/** Below this a press is a tap rather than a drag. A hand is never perfectly still. */
const DRAG_THRESHOLD_PX = 8;

/** How near the crosshair a press has to land to grab it instead of panning. */
const TARGET_GRAB_RADIUS_PX = 22;

/**
 * A hole with no bends mapped, shared so a default `[]` does not make a new array every render
 * and invalidate every memo that reads it.
 */
const NO_TURNS: LatLon[] = [];

/** Floating-point slack when asking "is this zoomed in at all?". */
const ZOOM_EPSILON = 0.001;

/** Half the box the facing cone is drawn in; its apex sits at the centre, on the player. */
const HEADING_CONE_RADIUS = 26;

/** Inset of the loading and unavailable overlays from the canvas edge. */
const OVERLAY_PADDING = 24;

/**
 * How much of the screen the flyover is allowed to take. It plays over the whole view
 * rather than inside the map canvas — a hole flown from the tee is the picture, and boxing
 * it into the map's corner of the card left it a stamp a few centimetres tall. The margin
 * that remains is what keeps the close button clear of the video's own controls.
 */
const FLYOVER_WIDTH_FRACTION = 0.96;
const FLYOVER_HEIGHT_FRACTION = 0.88;

/**
 * Height the scrub row occupies below the picture — a 34pt button plus the bar's own
 * padding. Taken off the video's box rather than added under it: the two are centred as one
 * column, and a full-height portrait render would otherwise push the row off the screen.
 */
const FLYOVER_CONTROLS_HEIGHT = 54;

/**
 * The ink the video paints its own tee-half numbers in (_MARKER_YELLOW in the analyser's
 * flyover.py, BGR there), so the readout above the thumb says which of the two a number is
 * the same way the ground does.
 */
const FLYOVER_TEE_DISTANCE_INK = appColors.status.warningText;

/** Strong enough to read the shape at a glance, sheer enough to leave the imagery visible. */
const OVERLAY_FILL_OPACITY = 0.28;

/** One stable empty array, so a caller with no lines never invalidates the projection memo. */
const NO_LINES: HoleLine[] = [];

/**
 * The hazard kinds worth a distance readout. `HAZARD_KINDS` (from `holeShapes`) also includes
 * the green surface, for draw depth — "165 m to the green" would only duplicate the headline
 * distance, not add to it.
 */
const HAZARD_LIST_KINDS = ['bunker', 'water'];

/**
 * How far ahead a hazard can be and still get a distance readout. Within this window every
 * bunker and pond is a live club decision and gets a number; beyond it the number decides
 * nothing about the shot at hand and the chips would only crowd the map.
 */
const HAZARD_AHEAD_WINDOW_M = 250;

/** Clearance between a hazard's own outline and the distance chip beside it. */
const HAZARD_LABEL_GAP_PX = 6;

export type HoleViewModalProps = {
  visible: boolean;
  onClose: () => void;
  holeNumber: number;
  /**
   * Par, where the scorecard has it. Only the flyover's distance readout uses it, and only
   * to pick which end of the hole to count from — unknown reads as a count-down to the
   * green, exactly as the rendered video paints an unknown par.
   */
  par?: number | null;
  tees: TeePoint[];
  green: LatLon | null;
  /**
   * Where the hole bends, in playing order — the turning points somebody mapped it with.
   *
   * The line is drawn round them instead of straight through them, and the player can take
   * hold of one to plan a different route. That is all a drag does: it moves the line on this
   * screen, for this visit, and never touches the course's own geometry, which is an admin's
   * to state and not something a round should be able to rewrite.
   */
  turns?: LatLon[];
  loading: boolean;
  /** The hole has been looked up and has no mapped green. */
  unavailable: boolean;
  /**
   * Mapped boundaries reaching into this hole, in draw order. With at least one bunker,
   * pond or green surface among them the player is offered the basemap choice; without
   * them the view is the imagery it has always been.
   */
  shapes?: HoleShape[];
  /** Out-of-bounds lines reaching into this hole. Drawn as a white dashed stroke over the imagery. */
  lines?: HoleLine[];
  player?: PlayerPosition | null;
  positionStatus?: PlayerPositionStatus;
  /** Compass heading in degrees clockwise from north, when the device offers a usable one. */
  heading?: number | null;
  wind?: Wind | null;
  /**
   * Terrain heights along this hole. Absent on every course the height model does not
   * cover, which is most of them, and the height readout simply does not appear.
   */
  elevation?: HoleElevationProfile | null;
  /**
   * The course behind this hole, once the round has resolved it. Enables the admin
   * data-analysis link and the player's report-inaccurate action; without it both hide.
   */
  courseId?: string | null;
  /** Needed only by the report-inaccurate action. */
  apiBaseUrl?: string;
  token?: string | null;
  /** Full scorecard hole count, so the admin hole-map view opened from here can step through every hole, not just this one. */
  holesCount?: number;
  /**
   * Step to the adjacent hole without leaving the view. Omitted at the ends of the round —
   * hole 1 has no previous, and the last hole's "what's next" is finishing the round, which
   * lives outside this modal — so the corresponding button hides rather than disabling.
   */
  onPreviousHole?: () => void;
  onNextHole?: () => void;
};

/**
 * Aerial view of one hole, framed from the tees to the green.
 *
 * The imagery is a mosaic of raster tiles rather than a map component: the app ships no
 * map SDK, and a static frame is all this needs. Everything drawn on top — the green
 * centre, the tee boxes, the player, the lines between them — is positioned by projecting
 * the same coordinates through the layout that placed the tiles.
 *
 * The headline number is what a golf rangefinder shows: metres from where the player is
 * standing to the centre of the green. Off the hole — or with no fix at all — it falls back
 * to the hole's own length from the back tee, which is the number on the card.
 */
export function HoleViewModal({
  visible,
  onClose,
  holeNumber,
  par = null,
  tees,
  green,
  turns = NO_TURNS,
  loading,
  unavailable,
  shapes = [],
  lines = NO_LINES,
  player = null,
  positionStatus = 'idle',
  heading = null,
  wind = null,
  elevation = null,
  courseId = null,
  apiBaseUrl = '',
  token = null,
  holesCount = 0,
  onPreviousHole,
  onNextHole,
}: HoleViewModalProps) {
  const { t } = useI18n();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const [courseHoleMapOpen, setCourseHoleMapOpen] = useState(false);
  const courseHoleMapHoleNumbers = useMemo(
    () => (holesCount > 0 ? Array.from({ length: holesCount }, (_, index) => index + 1) : [holeNumber]),
    [holesCount, holeNumber],
  );
  /** 'idle' | 'sending' | 'done' — resets when the hole changes so each hole can be flagged. */
  const [reportState, setReportState] = useState<'idle' | 'sending' | 'done'>('idle');
  useEffect(() => setReportState('idle'), [holeNumber]);

  /** Pre-rendered flyover MP4s, keyed by hole — most courses have none, and the icon
   * simply does not appear. */
  const flyoverByHole = useFlyoverVideos(
    apiBaseUrl,
    token,
    courseId,
    Boolean(visible && courseId && apiBaseUrl)
  );
  // The 3:5 phone render is the one cut for this view; an older course that only has the
  // 16:9 video plays that instead, in its own wider box.
  const flyoverVideo = flyoverByHole[holeNumber] ?? null;
  const flyoverUrl = flyoverVideo ? (flyoverVideo.portrait?.url ?? flyoverVideo.url) : null;
  const declaredFlyoverAspectRatio = flyoverVideo?.portrait
    ? flyoverVideo.portrait.width / flyoverVideo.portrait.height
    : 16 / 9;
  // The manifest's dimensions carry the box only until the player reports the file's real
  // ones: a render made to a different spec than the manifest recorded would otherwise be
  // fitted to a shape it does not have.
  const [measuredFlyoverAspectRatio, setMeasuredFlyoverAspectRatio] = useState<number | null>(null);
  useEffect(() => setMeasuredFlyoverAspectRatio(null), [flyoverUrl]);
  const flyoverAspectRatio = measuredFlyoverAspectRatio ?? declaredFlyoverAspectRatio;
  /** Whether the overlay is up. Whether the picture is moving is `flyoverPaused`. */
  const [flyoverPlaying, setFlyoverPlaying] = useState(false);
  useEffect(() => setFlyoverPlaying(false), [holeNumber, visible]);
  const [flyoverPaused, setFlyoverPaused] = useState(false);
  const flyoverVideoRef = useRef<Video>(null);
  // Taking the bar stops the picture and leaves it stopped: a scrub is someone looking for
  // a frame, and playback resuming under the finger would carry them past it.
  const flyoverScrub = useVideoScrub(flyoverVideoRef, {
    onScrubStart: () => setFlyoverPaused(true),
  });
  const resetFlyoverScrub = flyoverScrub.reset;
  /**
   * The distance the camera is at, above the thumb — the same number the bands painted into
   * the ground are counting. The renderer publishes the track; nothing here works out the
   * flight, and a course rendered before it existed simply shows no distance.
   */
  const flyoverDistance = useMemo(
    () =>
      flyoverVideo?.distances
        ? readFlyoverDistance(flyoverVideo.distances, flyoverScrub.progress, par)
        : null,
    [flyoverVideo, flyoverScrub.progress, par]
  );
  // Each opening starts at the tee, playing — the overlay unmounts the player, so the bar
  // would otherwise still read the position the last flight ended on.
  useEffect(() => {
    setFlyoverPaused(false);
    resetFlyoverScrub();
  }, [flyoverUrl, flyoverPlaying, resetFlyoverScrub]);

  /** Collapsed by default so the map gets the space; the toggle itself stays on screen. */
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  /** Defaults on, matching the always-visible text row this replaces. Local like the
   * disclaimer toggle, not the basemap store: a per-visit preference, not one worth
   * remembering across holes or app restarts. */
  const [showHazardLabels, setShowHazardLabels] = useState(true);

  const canReport = Boolean(courseId && token && apiBaseUrl);
  const submitReport = async () => {
    if (!canReport || reportState !== 'idle') return;
    setReportState('sending');
    const ok = await reportHoleInaccurate(apiBaseUrl, token, courseId, holeNumber);
    // Failure returns to idle so the player can retry; there is nothing else to tell them.
    setReportState(ok ? 'done' : 'idle');
  };

  /**
   * Admin-only, one tap from the round rather than a trip to the course-management screens.
   * Not reset on `holeNumber`, unlike `reportState` above — this queues the *course's*
   * analysis, not anything scoped to the hole being looked at, so walking to the next tee
   * must not make a just-queued run look untriggered again.
   */
  const [reanalyzeState, setReanalyzeState] = useState<'idle' | 'sending' | 'queued' | 'unavailable'>(
    'idle'
  );
  const canReanalyze = isAdmin && !!courseId;
  // A second tap landing before the first has re-rendered would read `reanalyzeState` as
  // still 'idle' — state updates are not visible to the same synchronous batch that queued
  // them — so the guard is a ref, which is.
  const reanalyzeInFlightRef = useRef(false);
  const submitReanalyze = async () => {
    if (!canReanalyze || reanalyzeInFlightRef.current) return;
    reanalyzeInFlightRef.current = true;
    setReanalyzeState('sending');
    const outcome = await requestCourseAnalysis(apiBaseUrl, token, courseId);
    // A transient failure goes back to idle so a second tap is all retrying takes; queued and
    // unavailable are both worth leaving on screen instead.
    setReanalyzeState(outcome === 'failed' ? 'idle' : outcome);
    reanalyzeInFlightRef.current = false;
  };

  /**
   * Course-level imagery-analysis state — not the picture itself, just whether analysis has
   * ever run, for the auto-request effect below. Only fetched while the view is actually
   * open, matching the course-configuration screen's own "held off until the panel is open"
   * rule for this same endpoint.
   */
  const courseAnalysis = useCourseAnalysisStatus(
    apiBaseUrl,
    token,
    courseId,
    Boolean(visible && courseId && apiBaseUrl)
  );

  /**
   * This hole's own cropped Lantmateriet basemap picture, when one has been stored — see
   * tileSource.ts's `LANTMATERIET_ATTRIBUTION` for why it can be shown at all. A small
   * per-hole picture, not the whole course's: course-analysis-service crops one per hole at
   * analysis time specifically so a phone never downloads more than the hole it is actually
   * looking at.
   */
  const holeBasemap = useHoleBasemapImage(
    apiBaseUrl,
    token,
    courseId,
    holeNumber,
    Boolean(visible && courseId && apiBaseUrl)
  );
  const lantmaterietImage = holeBasemap.image;

  /**
   * A course this analyser has never even been asked about — not "failed", not "queued
   * already" — gets one automatic nudge so the gap closes on its own instead of waiting for
   * an admin to happen to open this same view or the course-management screen. Guarded by a
   * ref rather than `reanalyzeState`: this can fire for any player, not just an admin, so it
   * must not share state with (or be gated by) the admin button above. Never retried here —
   * `abandoned` (three failed attempts) stays a deliberate admin retry, not something every
   * player re-triggers on every visit to a course whose imagery genuinely cannot be analysed.
   */
  const autoAnalyzeRequestedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!courseId || !token || !apiBaseUrl) return;
    if (courseAnalysis.loading || courseAnalysis.error) return;
    if (courseAnalysis.status?.state !== 'neverRequested') return;
    if (autoAnalyzeRequestedRef.current.has(courseId)) return;
    autoAnalyzeRequestedRef.current.add(courseId);
    void requestCourseAnalysis(apiBaseUrl, token, courseId).then((outcome) => {
      if (outcome === 'queued') courseAnalysis.refresh();
    });
  }, [
    apiBaseUrl,
    courseAnalysis.error,
    courseAnalysis.loading,
    courseAnalysis.refresh,
    courseAnalysis.status,
    courseId,
    token,
  ]);

  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  // The largest box of the video's shape that fits the screen, sized explicitly: the box is
  // what gives the picture its shape, so `CONTAIN` has nothing left to letterbox.
  const flyoverBox = useMemo(
    () =>
      resolveInfoVideoBox(
        flyoverAspectRatio,
        windowWidth * FLYOVER_WIDTH_FRACTION,
        windowHeight * FLYOVER_HEIGHT_FRACTION - FLYOVER_CONTROLS_HEIGHT
      ),
    [flyoverAspectRatio, windowHeight, windowWidth]
  );
  /** The spot the player tapped to measure a lay-up or a carry. */
  const [target, setTarget] = useState<LatLon | null>(null);
  /**
   * The player's own route, once they have moved a bend — null until then, meaning "as mapped".
   *
   * Local and short-lived on purpose. The mapped turning points are the club's geometry, an
   * admin's to state; a player dragging one is planning their own way round, not correcting
   * the course. So this is state on this screen, cleared with the hole exactly like `target`,
   * and nothing here writes to the geometry it started from.
   */
  const [route, setRoute] = useState<LatLon[] | null>(null);
  /** Zoom and pan on top of the framing that fits the hole. */
  const [view, setView] = useState<ViewTransform>(IDENTITY_VIEW);
  /**
   * What the hole is drawn on. Read from a store that persists it, not local state:
   * deliberately not reset with the hole, nor with the app being closed and reopened — a
   * player who prefers hazards over bare imagery prefers it on the next tee too, and on the
   * next round, and re-picking it every time is nobody's idea of a preference.
   */
  const basemap = useHoleBasemap();
  const setBasemap = useHoleBasemapState((state) => state.setBasemap);
  /** Live two-finger gesture, held in a ref so a pinch does not re-render per frame. */
  const pinchRef = useRef<{
    distance: number;
    midX: number;
    midY: number;
    view: ViewTransform;
  } | null>(null);
  /** The single-finger gesture in progress, and what it has turned out to be. */
  const gestureRef = useRef<{
    mode: 'pending' | 'target' | 'pan' | 'pinch' | 'turn';
    startX: number;
    startY: number;
    startView: ViewTransform;
    /** Which bend a 'turn' drag has hold of: its index among the hole's turning points. */
    turnIndex?: number;
  } | null>(null);

  // A target and a zoom belong to one hole and one visit. Walking to the next hole or
  // closing the view must not leave a stale aim point measuring against different geometry,
  // nor open the next hole already zoomed into the corner of the last one.
  useEffect(() => {
    setTarget(null);
    // A route planned on the last hole says nothing about this one, and leaving it behind
    // would draw that hole's bends across this hole's ground.
    setRoute(null);
    setView(IDENTITY_VIEW);
    pinchRef.current = null;
    gestureRef.current = null;
  }, [holeNumber, visible]);

  const onCanvasLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    // Sub-pixel jitter on web would otherwise recompute the whole mosaic on every frame.
    const next = { width: Math.round(width), height: Math.round(height) };
    setSize((current) =>
      current.width === next.width && current.height === next.height ? current : next
    );
  };

  // Only a player who is actually on this hole is drawn, and only then does the frame widen
  // to include them. Someone in the clubhouse would otherwise zoom the hole down to nothing.
  const playerOnHole = useMemo(() => {
    if (!player || !green || tees.length === 0) return false;
    const nearestTee = tees.reduce((closest, tee) =>
      distanceMeters(player, tee) < distanceMeters(player, closest) ? tee : closest
    );
    return distanceToSegmentMeters(player, nearestTee, green) <= ON_HOLE_RADIUS_METERS;
  }, [green, player, tees]);

  /** The tee the hole plays longest from, which is the one the card measures. */
  const backTee = useMemo(() => {
    if (!green || tees.length === 0) return null;
    return tees.reduce((longest, tee) =>
      distanceMeters(tee, green) > distanceMeters(longest, green) ? tee : longest
    );
  }, [green, tees]);

  /**
   * The bearing the view is turned to, so the hole plays bottom to top.
   *
   * Null until there is a hole to point along, which leaves the view north-up rather than
   * spinning it to an arbitrary angle while the geometry loads.
   */
  const holeUpBearing = useMemo(
    () => (backTee && green ? bearingDegrees(backTee, green) : null),
    [backTee, green]
  );

  const fitted = useMemo(
    () =>
      computeHoleViewLayout({
        tees,
        green,
        width: size.width,
        height: size.height,
        extraPoints: playerOnHole && player ? [player] : [],
        holeBearing: holeUpBearing,
        // Applied on every basemap, not just the two that draw the picture, so switching
        // to the drawing and back does not reframe the hole.
        coverage: lantmaterietImage,
      }),
    [green, holeUpBearing, lantmaterietImage, player, playerOnHole, size.height, size.width, tees]
  );

  const layout = useMemo(
    () => (fitted ? applyViewTransform(fitted, view) : null),
    [fitted, view]
  );

  const tileTemplate = useMemo(() => getSatelliteTileUrlTemplate(), []);
  const attribution = useMemo(() => getSatelliteAttribution(), []);

  /**
   * The choice is offered only where there is something to choose between.
   *
   * Bunkers, water and the green surface are what a player reads a hole by; a course with
   * only fairway cut from imagery has nothing to add to the picture, and three buttons that
   * change almost nothing are worse than none.
   */
  const hazardShapes = useMemo(
    () => shapes.filter((shape) => HAZARD_KINDS.includes(shape.kind)),
    [shapes]
  );
  const canChooseBasemap = hazardShapes.length > 0;
  // The hazards basemap also keeps the roads and cart paths: they are how a player finds their
  // way round the hole, and drawn first they sit under every hazard. They add no distance chip.
  const hazardBasemapShapes = useMemo(
    () =>
      shapes.filter(
        (shape) => HAZARD_KINDS.includes(shape.kind) || PATH_KINDS.includes(shape.kind)
      ),
    [shapes]
  );
  // Walking onto a hole with nothing mapped falls back to the imagery rather than to an
  // empty green rectangle, and the choice comes back on the next hole that has boundaries.
  const activeBasemap: HoleBasemap = canChooseBasemap ? basemap : 'satellite';

  /**
   * The rings in the mosaic's own pixel space, ready to draw.
   *
   * Projected through the same layout that placed the tiles, so a boundary lands on the
   * ground it was cut from — and turns with the map when the hole runs up the page.
   */
  const projectedShapes = useMemo(() => {
    if (!layout || activeBasemap === 'satellite') return [];
    const wanted = activeBasemap === 'hazards' ? hazardBasemapShapes : shapes;
    return wanted.map((shape) => ({
      id: shape.id,
      kind: shape.kind,
      points: shape.ring
        .map((vertex) => projectToLayout(vertex, layout))
        .map((point) => `${point.x},${point.y}`)
        .join(' '),
    }));
  }, [activeBasemap, hazardBasemapShapes, layout, shapes]);

  /** The out-of-bounds lines in the mosaic's pixel space, projected like the rings above. */
  const projectedLines = useMemo(() => {
    if (!layout) return [];
    return lines.map((line) => ({
      id: line.id,
      points: line.path
        .map((vertex) => projectToLayout(vertex, layout))
        .map((point) => `${point.x},${point.y}`)
        .join(' '),
    }));
  }, [layout, lines]);

  const teePoints = useMemo(
    () => (layout ? tees.map((tee) => projectToLayout(tee, layout)) : []),
    [layout, tees]
  );
  const greenPoint = useMemo(
    () => (layout && green ? projectToLayout(green, layout) : null),
    [green, layout]
  );
  const playerPoint = useMemo(
    () => (layout && player && playerOnHole ? projectToLayout(player, layout) : null),
    [layout, player, playerOnHole]
  );
  // The accuracy circle is drawn to scale, so a poor fix visibly reads as a poor fix rather
  // than as a confident dot in the wrong place.
  const accuracyRadiusPx = useMemo(() => {
    if (!layout || !player?.accuracy || !playerOnHole) return null;
    const radius = metersToWorldFraction(player.accuracy, player.lat) * layout.scale;
    // Below the marker's own size it says nothing, so it is left off rather than drawn inside.
    return radius > 12 ? radius : null;
  }, [layout, player, playerOnHole]);

  const targetPoint = useMemo(
    () => (layout && target ? projectToLayout(target, layout) : null),
    [layout, target]
  );

  const distanceUnit = useDistanceUnit();

  /** The bends being drawn: the player's own where they have moved one, else as mapped. */
  const shownTurns = route ?? turns;

  /**
   * The line the hole is played along: back tee, its bends, the green.
   *
   * Empty until both ends are known. A hole with no bends is the two-point chord it has
   * always been, so everything downstream of this is the same picture it drew before.
   */
  const holePath = useMemo(
    () => (backTee && green ? [backTee, ...shownTurns, green] : []),
    [backTee, green, shownTurns]
  );

  /**
   * One leg per span of the line, with where to write its distance.
   *
   * This is what the bends are for: a dogleg is played as two shots, and "how far to the
   * corner, how far from there" is the question the single tee-to-green number never answered.
   * A hole with no bends has one leg, which is that same number, so nothing new is written on
   * a straight hole.
   */
  const legs = useMemo(
    () =>
      holePath.slice(1).map((point, index) => ({
        from: holePath[index],
        to: point,
        meters: Math.round(distanceMeters(holePath[index], point)),
      })),
    [holePath]
  );

  /**
   * How long the hole plays: the walk round its bends, not the crow's flight between its ends.
   *
   * A dogleg measured straight reads short by however far it turns, which is the number the
   * scorecard has never agreed with. On a hole with no bends this is the chord it always was.
   */
  const holeMeters = useMemo(
    () =>
      legs.length > 0 ? Math.round(legs.reduce((total, leg) => total + leg.meters, 0)) : null,
    [legs]
  );

  /** The playing line on screen: back tee, bends, green. Empty until both ends are known. */
  const pathPoints = useMemo(
    () => (layout ? holePath.map((point) => projectToLayout(point, layout)) : []),
    [holePath, layout]
  );

  /**
   * What a rangefinder would read: the player to the middle of the green.
   *
   * The value itself is not gated on being on the hole — standing off the fairway, behind the
   * tee, or on the wrong side of a dogleg are all ordinary places to want this number, and a
   * golfer who is 30 m off the line of play is still playing the hole. What *is* gated on it is
   * the headline below: off the hole entirely (the clubhouse, another hole, a device with no
   * real fix yet) a straight line to the green is not a distance worth reading, so the headline
   * falls back to the hole's own length instead of this.
   */
  const toGreenMeters = useMemo(
    () => (green && player ? Math.round(distanceMeters(player, green)) : null),
    [green, player]
  );

  /**
   * Where a shot to the target is measured from.
   *
   * The player when they are on the hole, and the back tee otherwise — planning a hole from
   * the sofa is a real use, and quoting it from the tee is the only honest answer available.
   */
  const measureFrom = playerOnHole && player ? player : backTee;
  const measuringFromPlayer = playerOnHole && !!player;

  /**
   * The wind resolved against this hole's line of play.
   *
   * A bearing on its own tells a golfer nothing — "westerly" only becomes useful once you
   * know which way the hole runs — so the badge reports helping/into/across instead, and the
   * arrow points the way the wind actually blows on a north-up map.
   */
  const holeWind = useMemo(() => {
    if (!wind || !green || !backTee) return null;
    return relativeWind(wind, bearingDegrees(backTee, green));
  }, [backTee, green, wind]);

  /**
   * The climb or drop to the green, measured from wherever the headline distance is.
   *
   * Tied to `measureFrom` on purpose: the two numbers describe one shot, and a height read
   * from the tee beside a distance read from the player would be quietly describing two
   * different shots. Null on a course with no stored profile, and null again when the
   * player has wandered further off the line than the profile can answer for.
   */
  const holeElevation = useMemo(() => {
    if (!elevation || !green || !measureFrom) return null;
    return heightDifference(elevation, measureFrom, green);
  }, [elevation, green, measureFrom]);

  /** Pixel origin of the measured line: the player when they are on the hole, else the back tee. */
  const originPoint = useMemo(
    () => playerPoint ?? (layout && backTee ? projectToLayout(backTee, layout) : null),
    [backTee, layout, playerPoint]
  );

  const toTargetMeters = useMemo(
    () => (target && measureFrom ? Math.round(distanceMeters(measureFrom, target)) : null),
    [measureFrom, target]
  );
  const targetToGreenMeters = useMemo(
    () => (target && green ? Math.round(distanceMeters(target, green)) : null),
    [green, target]
  );

  /**
   * Distance to the nearest edge of every bunker and pond within reach of the next shot,
   * nearest first — not a tap-and-read like the target panel, since a hazard is exactly the
   * thing a player wants to see without having to already know roughly where its edge is to
   * tap near it.
   *
   * Within reach means ahead and inside {@link HAZARD_AHEAD_WINDOW_M}: a hazard already
   * walked past is no longer a decision, so it keeps its outline on the map but loses its
   * number. Ahead is read off the green — the hazard's edge is nearer the green than the
   * measure point is — which needs no bearing maths and treats a bunker tucked behind the
   * green as ahead, exactly as a player carrying the green over it would.
   *
   * From the same point everything else on this screen measures from: the player when they
   * are on the hole, the back tee otherwise.
   *
   * Only on a basemap that draws the hazard. The bare imagery outlines nothing, so the chip
   * floats over the photograph with no shape to belong to: the player reads a number without
   * being told which patch of sand or water it measures to, which is worse than no number at
   * all. Pick `hazards` or `drawing` and the outlines — and their distances — come back.
   */
  const hazardDistances = useMemo(() => {
    if (activeBasemap === 'satellite') return [];
    if (!measureFrom || !green) return [];
    const fromToGreen = distanceMeters(measureFrom, green);
    return shapes
      .filter((shape) => HAZARD_LIST_KINDS.includes(shape.kind))
      .map((shape) => ({
        id: shape.id,
        kind: shape.kind,
        meters: Math.round(distanceToRingMeters(measureFrom, shape.ring)),
        metersToGreen: distanceToRingMeters(green, shape.ring),
        anchor: ringCentroid(shape.ring),
      }))
      .filter((hazard): hazard is typeof hazard & { anchor: LatLon } => hazard.anchor !== null)
      .filter(
        (hazard) => hazard.meters <= HAZARD_AHEAD_WINDOW_M && hazard.metersToGreen < fromToGreen
      )
      .sort((a, b) => a.meters - b.meters);
  }, [activeBasemap, green, measureFrom, shapes]);

  /**
   * Screen position for each hazard label, in the same pixel space as the tee and green
   * markers. Pinned just outside the shape's own right edge rather than its centroid — a
   * number sat over the shape hid exactly the sand or water it was labelling, which is the
   * one thing on the map a player least wants covered up. Measured in projected pixels, not
   * lat/lon, so "right" means right on screen even when the view is turned to run hole-up.
   *
   * Not gated on which edge faces the player, unlike the nearest-edge point `meters` measures
   * to — a label that moved around the hazard depending on where the player stood would be
   * harder to find at a glance than one that always sits in the same place.
   */
  const hazardLabelPoints = useMemo(() => {
    if (!layout) return [];
    return hazardDistances.map((hazard) => {
      const ring = shapes.find((shape) => shape.id === hazard.id)?.ring ?? [];
      const projectedRing = ring.map((vertex) => projectToLayout(vertex, layout));
      const point =
        pointBesideRing(projectedRing, HAZARD_LABEL_GAP_PX) ?? projectToLayout(hazard.anchor, layout);
      return { ...hazard, point };
    });
  }, [hazardDistances, layout, shapes]);

  /**
   * The rangefinder number is only trustworthy while the player is actually on the hole —
   * off it, `toGreenMeters` is a straight line to wherever the green happens to be, which can
   * read as an absurd distance from the clubhouse, the wrong hole, or (in testing) another
   * continent. Off-hole, the headline falls back to the same back-tee length the card shows,
   * matching what `hole.holeViewOffHole` below tells the player is happening.
   */
  const headlineMeters = playerOnHole ? toGreenMeters ?? holeMeters : holeMeters;
  const headlineLabel =
    playerOnHole && toGreenMeters !== null ? 'hole.holeViewToGreen' : 'hole.holeViewHoleLength';

  /**
   * Why the headline is not measuring from the player, when it is not.
   *
   * Silence would read as "the app cannot do this"; naming the reason tells the player
   * whether it is worth turning something on. Nothing is said once the distance is live, and
   * nothing is said when there is no geometry to measure against in the first place.
   */
  const positionNoteKey = useMemo(() => {
    if (!green || toGreenMeters !== null) return null;
    if (positionStatus === 'denied') return 'hole.holeViewLocationDenied';
    if (positionStatus === 'unavailable') return 'hole.holeViewLocationUnavailable';
    if (positionStatus === 'locating' || (positionStatus === 'tracking' && !player)) {
      return 'hole.holeViewLocating';
    }
    return null;
  }, [green, player, positionStatus, toGreenMeters]);

  /**
   * Puts the aim point under the pointer, on the first touch and on every move after it.
   *
   * The canvas drives this through the responder system rather than a Pressable, for two
   * reasons. A drag needs the move events, which a press callback never sees. And the
   * responder event carries `locationX`/`locationY` on web as well as native — unlike the
   * click event behind `onPress`, which is what made the first version of tap-to-measure do
   * nothing in a browser.
   *
   * A press with no usable coordinates is ignored rather than guessed at; putting a target
   * in the wrong place is worse than none.
   */
  const canvasPointOf = (event: GestureResponderEvent) =>
    // `currentTarget` is the canvas element on web, and an opaque node handle on native
    // where the touch coordinates are used instead.
    pressPointInElement(
      event.nativeEvent as PressPointEvent,
      event.currentTarget as unknown as MeasurableElement
    );

  const placeTargetAt = (point: { x: number; y: number }) => {
    if (!layout) return;
    setTarget(unprojectFromLayout(viewportToLayoutPoint(point, layout), layout));
  };

  /**
   * Moves one bend of the player's route to where the finger is.
   *
   * The first drag is what makes a route at all: until then the line is the mapped one, and
   * `route` is null. It starts as a copy of the bends as mapped, so moving the second bend of
   * a dogleg leaves the first exactly where the course says it is.
   *
   * The mapped geometry is not touched. This is the player planning their way round the hole,
   * and the course's own line belongs to whoever mapped it.
   */
  const moveTurnTo = (index: number, point: { x: number; y: number }) => {
    if (!layout) return;
    const moved = unprojectFromLayout(viewportToLayoutPoint(point, layout), layout);
    setRoute((current) => (current ?? turns).map((turn, at) => (at === index ? moved : turn)));
  };

  const touchesOf = (event: GestureResponderEvent) =>
    (event.nativeEvent.touches ?? []) as Array<{ locationX?: number; locationY?: number }>;

  /**
   * Starts a gesture without committing to what it is yet.
   *
   * Nothing is placed on touch-down. That matters for more than tidiness: a pinch begins with
   * one finger, so placing on the first touch dropped an aim point every time the player went
   * to zoom, and another one when they lifted a finger at the end.
   *
   * Landing on the marker grabs it. Landing anywhere else stays undecided until the finger
   * either travels — pan when zoomed in, drag the aim point at the framing that fits the hole,
   * where there is nowhere to pan to — or lifts, which is a tap.
   */
  const onGestureStart = (event: GestureResponderEvent) => {
    if (touchesOf(event).length >= 2) {
      updatePinch(event);
      return;
    }

    const point = canvasPointOf(event);
    if (!point || !layout) return;

    /** How far the press landed from a marker on screen. */
    const reach = (at: { x: number; y: number }) => {
      const viewport = layoutToViewportPoint(at, layout);
      return Math.hypot(point.x - viewport.x, point.y - viewport.y);
    };

    // The aim point and the bends are all small targets, and they can sit close together —
    // a lay-up marked at the corner of a dogleg is the obvious case. So a press picks
    // whichever it is actually nearest to, rather than whichever happens to be tested first.
    const grabbed = [
      ...(targetPoint ? [{ mode: 'target' as const, distance: reach(targetPoint) }] : []),
      ...pathPoints.slice(1, -1).map((turn, index) => ({
        mode: 'turn' as const,
        turnIndex: index,
        distance: reach(turn),
      })),
    ]
      .filter((candidate) => candidate.distance <= TARGET_GRAB_RADIUS_PX)
      .sort((a, b) => a.distance - b.distance)[0];

    gestureRef.current = {
      mode: grabbed?.mode ?? 'pending',
      startX: point.x,
      startY: point.y,
      startView: view,
      turnIndex: grabbed && 'turnIndex' in grabbed ? grabbed.turnIndex : undefined,
    };
  };

  const onGestureMove = (event: GestureResponderEvent) => {
    if (touchesOf(event).length >= 2) {
      updatePinch(event);
      return;
    }

    const gesture = gestureRef.current;
    const point = canvasPointOf(event);
    if (!gesture || !point || gesture.mode === 'pinch') return;

    if (gesture.mode === 'pending') {
      const travelled = Math.hypot(point.x - gesture.startX, point.y - gesture.startY);
      // A hand is never perfectly still; below this a press is a tap, not a drag.
      if (travelled < DRAG_THRESHOLD_PX) return;
      gesture.mode = view.zoom > MIN_VIEW_ZOOM + ZOOM_EPSILON ? 'pan' : 'target';
    }

    if (gesture.mode === 'turn') {
      moveTurnTo(gesture.turnIndex ?? 0, point);
      return;
    }

    if (gesture.mode === 'target') {
      placeTargetAt(point);
      return;
    }

    setView({
      ...gesture.startView,
      panX: gesture.startView.panX + (point.x - gesture.startX),
      panY: gesture.startView.panY + (point.y - gesture.startY),
    });
  };

  /**
   * A press that never travelled is a tap, and a tap places the aim point.
   *
   * Placed from where the finger went down rather than where it came up, so the marker lands
   * under the fingertip the player was aiming with.
   */
  const onGestureEnd = () => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    pinchRef.current = null;
    if (gesture?.mode === 'pending') {
      placeTargetAt({ x: gesture.startX, y: gesture.startY });
    }
  };

  /**
   * Two fingers zoom and pan together.
   *
   * Measured against the frame the gesture started on rather than the previous one, so
   * rounding cannot accumulate across a long pinch.
   */
  const updatePinch = (event: GestureResponderEvent) => {
    const [a, b] = touchesOf(event);
    if (
      !Number.isFinite(a?.locationX) ||
      !Number.isFinite(a?.locationY) ||
      !Number.isFinite(b?.locationX) ||
      !Number.isFinite(b?.locationY)
    ) {
      return;
    }

    // Whatever the single-finger gesture was about to become, it is a pinch now.
    if (gestureRef.current) gestureRef.current.mode = 'pinch';

    const distance = Math.hypot(b.locationX! - a.locationX!, b.locationY! - a.locationY!);
    const midX = (a.locationX! + b.locationX!) / 2;
    const midY = (a.locationY! + b.locationY!) / 2;

    const start = pinchRef.current;
    if (!start || !(start.distance > 0)) {
      pinchRef.current = { distance, midX, midY, view };
      return;
    }

    setView({
      zoom: clampZoom((start.view.zoom * distance) / start.distance),
      panX: start.view.panX + (midX - start.midX),
      panY: start.view.panY + (midY - start.midY),
    });
  };

  /** Steps the zoom about the middle of the view, which is where the buttons imply. */
  const stepZoom = (factor: number) =>
    setView((current) => ({
      ...current,
      zoom: clampZoom(current.zoom * factor),
    }));

  const zoomedIn = view.zoom > MIN_VIEW_ZOOM + ZOOM_EPSILON;

  return (
    <>
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <View style={styles.header}>
            <View style={styles.headerRow}>
              <View style={styles.titleRow}>
                {onPreviousHole ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t('hole.prevHole')}
                    testID="hole-view-prev-hole"
                    style={styles.holeStepButton}
                    onPress={onPreviousHole}
                  >
                    <Text style={styles.holeStepButtonText}>‹</Text>
                  </Pressable>
                ) : null}
                <Text style={styles.title}>{t('hole.holeViewTitle', { hole: holeNumber })}</Text>
                {onNextHole ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t('hole.nextHole')}
                    testID="hole-view-next-hole"
                    style={styles.holeStepButton}
                    onPress={onNextHole}
                  >
                    <Text style={styles.holeStepButtonText}>›</Text>
                  </Pressable>
                ) : null}
                <View style={styles.betaPill} testID="hole-view-beta-pill">
                  <Text style={styles.betaPillText}>{t('hole.holeViewBeta')}</Text>
                </View>
              </View>
              {headlineMeters !== null ? (
                <View style={styles.readout}>
                  <Text style={styles.readoutValue} testID="hole-view-headline-distance">
                    {formatHoleDistance(headlineMeters, distanceUnit)}
                  </Text>
                  <Text style={styles.readoutLabel}>{t(headlineLabel)}</Text>
                </View>
              ) : null}
            </View>
            {/* The card number stays visible once the headline is measuring from the player,
                so the two are never confused for each other. Off the hole the headline has
                already fallen back to this same number, so showing it twice would just repeat
                the line below instead of adding to it. */}
            {playerOnHole && toGreenMeters !== null && holeMeters !== null ? (
              <Text style={styles.subtitle}>
                {t('hole.holeViewDistance', {
                  distance: formatHoleDistance(holeMeters, distanceUnit),
                })}
              </Text>
            ) : null}
            {/* Beneath the distance rather than on the map: the two are read together as
                one club decision, and a badge floating over the imagery would separate
                them. Level is worth saying out loud — it is the difference between flat
                ground and no reading at all. */}
            {holeElevation ? (
              <Text style={styles.subtitle} testID="hole-view-elevation">
                {holeElevation.descriptor === 'level'
                  ? t('hole.holeViewElevationLevel')
                  : t(
                      holeElevation.descriptor === 'uphill'
                        ? 'hole.holeViewElevationUphill'
                        : 'hole.holeViewElevationDownhill',
                      { change: formatElevation(holeElevation.deltaM, distanceUnit) }
                    )}
              </Text>
            ) : null}
            {positionNoteKey ? (
              <Text style={styles.subtitle} testID="hole-view-position-note">
                {t(positionNoteKey)}
              </Text>
            ) : null}
            {toGreenMeters !== null && !playerOnHole ? (
              <Text style={styles.subtitle} testID="hole-view-off-hole-note">
                {t('hole.holeViewOffHole')}
              </Text>
            ) : null}
          </View>

          <View
            style={styles.canvas}
            onLayout={onCanvasLayout}
            testID="hole-view-canvas"
            accessibilityLabel={t('hole.holeViewTargetHint')}
            // Claim the gesture on touch and on a drag that began elsewhere, so the target
            // follows the finger instead of only landing where it first went down.
            onStartShouldSetResponder={() => true}
            onMoveShouldSetResponder={() => true}
            onResponderGrant={onGestureStart}
            onResponderMove={onGestureMove}
            onResponderRelease={onGestureEnd}
            onResponderTerminate={onGestureEnd}
            // Nothing else on this screen wants the gesture; letting it go mid-drag would
            // strand the target halfway.
            onResponderTerminationRequest={() => false}
          >
            {layout ? (
              <View
                testID="hole-view-rotator"
                pointerEvents="none"
                style={{
                  position: 'absolute',
                  left: (layout.viewportWidth - layout.width) / 2,
                  top: (layout.viewportHeight - layout.height) / 2,
                  width: layout.width,
                  height: layout.height,
                  transform: [{ rotate: `${layout.rotationDegrees}deg` }],
                }}
              >
                {/* Tiles are skipped entirely on the drawing, rather than hidden under it:
                    the drawing is the one view that costs no imagery, which matters on a
                    course with no signal to spare. A stored Lantmateriet basemap, where one
                    exists, replaces the live tile grid rather than layering under or over it
                    — one basemap picture, not two competing ones. */}
                {activeBasemap === 'drawing'
                  ? null
                  : lantmaterietImage
                    ? (() => {
                        const topLeft = projectToLayout(
                          { lat: lantmaterietImage.north, lon: lantmaterietImage.west },
                          layout
                        );
                        const bottomRight = projectToLayout(
                          { lat: lantmaterietImage.south, lon: lantmaterietImage.east },
                          layout
                        );
                        return (
                          <Image
                            testID="hole-view-lantmateriet-basemap"
                            source={{ uri: lantmaterietImage.url }}
                            style={{
                              position: 'absolute',
                              left: topLeft.x,
                              top: topLeft.y,
                              width: bottomRight.x - topLeft.x,
                              height: bottomRight.y - topLeft.y,
                            }}
                          />
                        );
                      })()
                    : layout.tiles.map((tile) => (
                        <Tile
                          key={`${tile.z}/${tile.x}/${tile.y}`}
                          url={buildTileUrl(tileTemplate, tile)}
                          testID={`hole-view-tile-${tile.z}-${tile.x}-${tile.y}`}
                          style={[
                            styles.tile,
                            {
                              left: tile.left,
                              top: tile.top,
                              width: layout.tileSize,
                              height: layout.tileSize,
                            },
                          ]}
                        />
                      ))}

                {projectedShapes.length > 0 || projectedLines.length > 0 || activeBasemap === 'drawing' ? (
                  <Svg
                    testID={
                      activeBasemap === 'drawing' ? 'hole-view-drawing' : 'hole-view-hazards'
                    }
                    pointerEvents="none"
                    style={StyleSheet.absoluteFill}
                    width={layout.width}
                    height={layout.height}
                  >
                    {/* Everything not otherwise mapped is rough. The rect covers the turned
                        box, which reaches the canvas corners at every rotation. */}
                    {activeBasemap === 'drawing' ? (
                      <Rect
                        x={0}
                        y={0}
                        width={layout.width}
                        height={layout.height}
                        fill={ROUGH_FILL}
                      />
                    ) : null}

                    {projectedShapes.map((shape) =>
                      activeBasemap === 'drawing' ? (
                        DRAWING_FILLS[shape.kind] ? (
                          <Polygon
                            key={shape.id}
                            points={shape.points}
                            fill={DRAWING_FILLS[shape.kind].fill}
                            stroke={DRAWING_FILLS[shape.kind].stroke}
                            strokeWidth={2}
                            strokeLinejoin="round"
                          />
                        ) : null
                      ) : (
                        <Polygon
                          key={shape.id}
                          points={shape.points}
                          stroke={OVERLAY_COLORS[shape.kind] ?? appColors.text.onMedia}
                          strokeWidth={2}
                          fill={OVERLAY_COLORS[shape.kind] ?? appColors.text.onMedia}
                          fillOpacity={OVERLAY_FILL_OPACITY}
                        />
                      )
                    )}

                    {/* Where the hole stops: a white dashed stroke with a dark halo under it,
                        so it reads over grass, sand and the drawing's flat fills alike. */}
                    {projectedLines.map((line) => (
                      <Polyline
                        key={`${line.id}-halo`}
                        points={line.points}
                        stroke={alpha(appColors.neutral.black, 0.55)}
                        strokeWidth={5}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        fill="none"
                      />
                    ))}
                    {projectedLines.map((line) => (
                      <Polyline
                        key={line.id}
                        testID={`hole-view-out-of-bounds-${line.id}`}
                        points={line.points}
                        stroke={appColors.text.onMedia}
                        strokeWidth={2.5}
                        strokeDasharray="10,7"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        fill="none"
                      />
                    ))}
                  </Svg>
                ) : null}

                {/* Just the number, no kind: the shape it sits beside is drawn in its own
                    colour, which already says bunker vs. water — a label a few characters
                    wide has no room to repeat it. */}
                {showHazardLabels &&
                  hazardLabelPoints.map((hazard) => (
                    <View
                      key={hazard.id}
                      testID={`hole-view-hazard-${hazard.id}`}
                      pointerEvents="none"
                      style={[styles.hazardMarker, { left: hazard.point.x, top: hazard.point.y }]}
                    >
                      <Text
                        style={[
                          styles.teeLabel,
                          { transform: [{ rotate: `${-layout.rotationDegrees}deg` }] },
                        ]}
                        numberOfLines={1}
                      >
                        {formatHoleDistance(hazard.meters, distanceUnit)}
                      </Text>
                    </View>
                  ))}

                {/* The hole as it is played: one span per leg, bending where it bends. A hole
                    with no turning points has a single span, which is the tee-to-green line
                    this has always drawn. Measured from the back tee, the same end the hole
                    length is quoted from, so the labels below belong to the line above them. */}
                {pathPoints.slice(1).map((point, index) => (
                  <HoleLine key={`hole-leg-${index}`} from={pathPoints[index]} to={point} />
                ))}

                {/* How far each leg plays. Only once the hole actually bends: on a straight
                    hole this is the headline number again, written a second time across the
                    middle of the fairway for no one's benefit. */}
                {legs.length > 1 &&
                  legs.map((leg, index) => {
                    const from = pathPoints[index];
                    const to = pathPoints[index + 1];
                    return (
                      <View
                        key={`hole-leg-label-${index}`}
                        pointerEvents="none"
                        style={[
                          styles.legDistance,
                          { left: (from.x + to.x) / 2 - 26, top: (from.y + to.y) / 2 - 10 },
                        ]}
                      >
                        <Text
                          testID={`hole-view-leg-distance-${index}`}
                          style={[
                            styles.legDistanceText,
                            { transform: [{ rotate: `${-layout.rotationDegrees}deg` }] },
                          ]}
                          numberOfLines={1}
                        >
                          {formatHoleDistance(leg.meters, distanceUnit)}
                        </Text>
                      </View>
                    );
                  })}

                {/* The bends themselves, one grab handle each. Drawn after the legs so a
                    handle is never hidden under the line it sits on. */}
                {pathPoints.slice(1, -1).map((point, index) => (
                  <View
                    key={`hole-turn-${index}`}
                    testID={`hole-view-turn-${index}`}
                    pointerEvents="none"
                    style={[styles.turnMarker, { left: point.x - 11, top: point.y - 11 }]}
                  >
                    <View style={styles.turnMarkerRing} />
                    <View style={styles.turnMarkerDot} />
                  </View>
                ))}

                {teePoints.map((point, index) => (
                  <View
                    key={`tee-${index}`}
                    testID={`hole-view-tee-${index}`}
                    style={[styles.teeMarkerGroup, { left: point.x - 7, top: point.y - 7 }]}
                    pointerEvents="none"
                  >
                    <View style={styles.teeMarker} />
                    {tees[index]?.label ? (
                      <Text
                        style={[
                          styles.teeLabel,
                          { transform: [{ rotate: `${-layout.rotationDegrees}deg` }] },
                        ]}
                        numberOfLines={1}
                      >
                        {tees[index].label}
                      </Text>
                    ) : null}
                  </View>
                ))}

                {greenPoint ? (
                  <View
                    testID="hole-view-green-marker"
                    style={[styles.greenMarker, { left: greenPoint.x - 22, top: greenPoint.y - 22 }]}
                    pointerEvents="none"
                  >
                    <View style={styles.greenMarkerRing} />
                    <View style={styles.greenMarkerDot} />
                  </View>
                ) : null}

                {/* With an aim point set, the live line goes through it rather than straight
                    to the green — the two legs are the two numbers being read. */}
                {targetPoint && originPoint ? (
                  <HoleLine from={originPoint} to={targetPoint} variant="toGreen" />
                ) : playerPoint && greenPoint ? (
                  <HoleLine from={playerPoint} to={greenPoint} variant="toGreen" />
                ) : null}

                {targetPoint && greenPoint ? (
                  <HoleLine from={targetPoint} to={greenPoint} variant="targetToGreen" />
                ) : null}

                {targetPoint ? (
                  <View
                    testID="hole-view-target-marker"
                    pointerEvents="none"
                    style={[styles.targetMarker, { left: targetPoint.x - 13, top: targetPoint.y - 13 }]}
                  >
                    <View style={styles.targetCrosshairVertical} />
                    <View style={styles.targetCrosshairHorizontal} />
                  </View>
                ) : null}

                {playerPoint ? (
                  <>
                    {accuracyRadiusPx ? (
                      <View
                        testID="hole-view-accuracy-ring"
                        pointerEvents="none"
                        style={[
                          styles.accuracyRing,
                          {
                            left: playerPoint.x - accuracyRadiusPx,
                            top: playerPoint.y - accuracyRadiusPx,
                            width: accuracyRadiusPx * 2,
                            height: accuracyRadiusPx * 2,
                            borderRadius: accuracyRadiusPx,
                          },
                        ]}
                      />
                    ) : null}
                    {heading === null ? null : (
                      <View
                        testID="hole-view-heading-cone"
                        pointerEvents="none"
                        style={[
                          styles.headingCone,
                          {
                            left: playerPoint.x - HEADING_CONE_RADIUS,
                            top: playerPoint.y - HEADING_CONE_RADIUS,
                            // The map may be turned to run hole-up, so the compass bearing is
                            // turned with it — the same correction the wind arrow makes.
                            transform: [{ rotate: `${heading + layout.rotationDegrees}deg` }],
                          },
                        ]}
                      >
                        <View style={styles.headingConeWedge} />
                      </View>
                    )}
                    <View
                      testID="hole-view-player-marker"
                      pointerEvents="none"
                      style={[styles.playerMarker, { left: playerPoint.x - 9, top: playerPoint.y - 9 }]}
                    />
                  </>
                ) : null}


              </View>
            ) : null}

            {layout ? (
              <>
                {/* Outside the turned mosaic: the badge has to stay upright and pinned to the
                    corner of the canvas, and the arrow carries the turn itself. */}
                {holeWind && wind ? (
                  <View style={styles.windBadge} testID="hole-view-wind" pointerEvents="none">
                    <View
                      testID="hole-view-wind-arrow"
                      style={[
                        styles.windArrow,
                        // The map may be turned to run hole-up, so the arrow is turned with
                        // it: on a hole-up view this is the wind relative to the shot.
                        {
                          transform: [
                            {
                              rotate: `${holeWind.towardDegrees + layout.rotationDegrees}deg`,
                            },
                          ],
                        },
                      ]}
                    >
                      <View style={styles.windArrowHead} />
                      <View style={styles.windArrowTail} />
                    </View>
                    <View>
                      <Text style={styles.windSpeed}>
                        {formatWindSpeed(wind.speedMps, distanceUnit)}
                      </Text>
                      <Text style={styles.windDescriptor}>
                        {t(`hole.holeViewWind_${holeWind.descriptor}`)}
                      </Text>
                    </View>
                  </View>
                ) : null}

                {/* Buttons as well as pinch: a pinch is two fingers, and there is no such
                    thing on a mouse. Every button inside the canvas is `cancelable={false}`:
                    the canvas claims the responder on any move, and an Android finger always
                    moves a pixel or two during a tap, so the press was handed to the canvas
                    and never fired. */}
                <View style={styles.zoomControls}>
                  <Pressable
                    cancelable={false}
                    accessibilityRole="button"
                    testID="hole-view-zoom-in"
                    style={styles.zoomButton}
                    disabled={view.zoom >= MAX_VIEW_ZOOM}
                    onPress={() => stepZoom(ZOOM_STEP)}
                  >
                    <Text style={styles.zoomButtonText}>+</Text>
                  </Pressable>
                  <Pressable
                    cancelable={false}
                    accessibilityRole="button"
                    testID="hole-view-zoom-out"
                    style={styles.zoomButton}
                    disabled={!zoomedIn}
                    onPress={() => stepZoom(1 / ZOOM_STEP)}
                  >
                    <Text style={styles.zoomButtonText}>−</Text>
                  </Pressable>
                  {zoomedIn ? (
                    <Pressable
                      cancelable={false}
                      accessibilityRole="button"
                      testID="hole-view-zoom-fit"
                      style={styles.zoomButton}
                      onPress={() => setView(IDENTITY_VIEW)}
                    >
                      <Text style={styles.zoomFitText}>{t('hole.holeViewZoomFit')}</Text>
                    </Pressable>
                  ) : null}
                </View>

                {/* Bottom-left, the one corner nothing else claims, and inside the canvas
                    so switching cannot resize it — a footer that grew would refit the frame
                    and move the map out from under the finger. */}
                {canChooseBasemap ? (
                  <View style={styles.basemapControls} testID="hole-view-basemap">
                    {BASEMAPS.map((option) => {
                      const selected = activeBasemap === option;
                      return (
                        <Pressable
                          key={option}
                          cancelable={false}
                          // One of three, exactly one on: a radio group, not three buttons.
                          // react-native-web 0.21 drops accessibilityState, so the state is
                          // given as aria-checked, which both platforms honour on RN 0.81.
                          accessibilityRole="radio"
                          aria-checked={selected}
                          testID={`hole-view-basemap-${option}`}
                          style={[styles.basemapButton, selected && styles.basemapButtonActive]}
                          onPress={() => setBasemap(option)}
                        >
                          <Text
                            style={[styles.basemapText, selected && styles.basemapTextActive]}
                          >
                            {t(`hole.holeViewBasemap_${option}`)}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                ) : null}

                {/* The imagery credit belongs to the imagery. On the drawing there is none
                    on screen to credit, and whichever picture is actually on screen —
                    stored Lantmateriet basemap or live Esri tiles — gets its own credit. */}
                {activeBasemap === 'drawing' ? null : (
                  <Text style={styles.attribution}>
                    {lantmaterietImage ? LANTMATERIET_ATTRIBUTION : attribution}
                  </Text>
                )}

                {/* Bottom-right, the one corner nothing else claims — only shown once the
                    laptop render script has actually produced a video for this hole. */}
                {flyoverUrl && !loading && !unavailable ? (
                  <Pressable
                    cancelable={false}
                    accessibilityRole="button"
                    accessibilityLabel={t('hole.holeViewFlyover')}
                    testID="hole-view-flyover-open"
                    style={styles.flyoverButton}
                    onPress={() => setFlyoverPlaying(true)}
                  >
                    <Ionicons name="videocam" size={16} color={appColors.text.primary} />
                  </Pressable>
                ) : null}
              </>
            ) : null}

            {loading ? (
              <View style={styles.overlay} testID="hole-view-loading">
                <ActivityIndicator color={appColors.accent.primary} />
              </View>
            ) : unavailable ? (
              <View style={styles.overlay} testID="hole-view-unavailable">
                <Text style={styles.overlayText}>{t('hole.holeViewUnavailable')}</Text>
              </View>
            ) : null}
          </View>

          {/* Grouped under its own tighter gap rather than `card`'s: these rows are read at a
              glance, not read — the 10 px `card` uses between the map and its header is
              legible breathing room there and just wasted height repeated four more times
              here, where every pixel is competing with the map for the same card. */}
          <View style={styles.infoStack}>
          {/* Fixed height: the readout panel is taller than the legend it replaces, and a
              footer that grew when the first target landed would resize the canvas, refit
              the frame and move the map out from under the finger mid-drag. */}
          <View style={styles.footerSlot}>
          {targetPoint && toTargetMeters !== null && targetToGreenMeters !== null ? (
            <View style={styles.targetPanel} testID="hole-view-target-panel">
              <View style={styles.targetLeg}>
                <Text style={styles.targetLegValue} testID="hole-view-target-carry">
                  {formatHoleDistance(toTargetMeters, distanceUnit)}
                </Text>
                <Text style={styles.targetLegLabel}>
                  {t(
                    measuringFromPlayer
                      ? 'hole.holeViewTargetFromYou'
                      : 'hole.holeViewTargetFromTee'
                  )}
                </Text>
              </View>
              <View style={styles.targetLegDivider} />
              <View style={styles.targetLeg}>
                <Text style={styles.targetLegValue} testID="hole-view-target-remaining">
                  {formatHoleDistance(targetToGreenMeters, distanceUnit)}
                </Text>
                <Text style={styles.targetLegLabel}>{t('hole.holeViewTargetToGreen')}</Text>
              </View>
              <Pressable
                accessibilityRole="button"
                testID="hole-view-target-clear"
                style={styles.targetClear}
                onPress={() => setTarget(null)}
              >
                <Text style={styles.targetClearText}>{t('hole.holeViewTargetClear')}</Text>
              </Pressable>
            </View>
          ) : greenPoint ? (
            <View style={styles.legend}>
              <View style={styles.legendItem}>
                <View style={styles.legendTee} />
                <Text style={styles.legendLabel}>{t('hole.holeViewTee')}</Text>
              </View>
              <View style={styles.legendItem}>
                <View style={styles.legendGreen} />
                <Text style={styles.legendLabel}>{t('hole.holeViewGreenCentre')}</Text>
              </View>
              {playerPoint ? (
                <View style={styles.legendItem}>
                  <View style={styles.legendPlayer} />
                  <Text style={styles.legendLabel}>{t('hole.holeViewYou')}</Text>
                </View>
              ) : null}
              {hazardDistances.length > 0 ? (
                <Pressable
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: showHazardLabels }}
                  accessibilityLabel={t('hole.holeViewHazardToggle')}
                  testID="hole-view-hazards-toggle"
                  style={styles.legendItem}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  onPress={() => setShowHazardLabels((shown) => !shown)}
                >
                  <View
                    style={[
                      styles.legendCheckbox,
                      showHazardLabels && styles.legendCheckboxChecked,
                    ]}
                  >
                    {showHazardLabels ? <Text style={styles.legendCheckboxMark}>✓</Text> : null}
                  </View>
                  <Text style={styles.legendLabel}>{t('hole.holeViewHazardToggle')}</Text>
                </Pressable>
              ) : null}
              <Text style={styles.legendHint} testID="hole-view-target-hint">
                {t('hole.holeViewTargetHint')}
              </Text>
            </View>
          ) : null}
          </View>

          {/* Collapsed by default so the map keeps the space; the toggle itself is always
              visible so the accuracy caveat stays one tap away rather than requiring the
              player to already know it exists. Sharing a row with the report link is the
              same trade: two lines of chrome the map would otherwise give up its own space
              for become one. */}
          <View style={styles.metaRow}>
            <Pressable
              accessibilityRole="button"
              testID="hole-view-disclaimer-toggle"
              style={styles.disclaimerToggle}
              onPress={() => setDisclaimerOpen((open) => !open)}
            >
              <Text style={styles.disclaimerToggleText}>
                {t('hole.holeViewDisclaimerToggle')} {disclaimerOpen ? '▲' : '▼'}
              </Text>
            </Pressable>

            {canReport ? (
              <Pressable
                accessibilityRole="button"
                testID="hole-view-report-inaccurate"
                disabled={reportState !== 'idle'}
                onPress={() => void submitReport()}
                style={styles.reportLink}
              >
                <Text
                  style={[styles.reportLinkText, reportState === 'done' && styles.reportLinkDone]}
                >
                  {reportState === 'done'
                    ? t('hole.holeViewReported')
                    : t('hole.holeViewReportInaccurate')}
                </Text>
              </Pressable>
            ) : null}
          </View>
          {disclaimerOpen ? (
            <>
              <Text style={styles.betaNotice} testID="hole-view-beta-notice">
                {t('hole.holeViewBetaNotice')}
              </Text>

              {/* The boundaries are a colour threshold over satellite imagery, and no human has
                  signed off on any of them. That is a weaker claim than the tee and green
                  positions, which players' own traces refine, so it is said out loud the moment
                  a player is looking at a shape rather than at the ground. */}
              {activeBasemap === 'satellite' ? null : (
                <Text style={styles.betaNotice} testID="hole-view-shapes-notice">
                  {t('hole.holeViewShapesNotice')}
                </Text>
              )}
            </>
          ) : null}
          </View>

          {isAdmin && courseId ? (
            <Pressable
              accessibilityRole="button"
              testID="hole-view-analysis"
              style={styles.analysisButton}
              onPress={() => {
                // Close first: only one <Modal> may be visible at a time (iOS silently drops
                // a second simultaneous one), so the hole view closes before the hole-map
                // modal opens rather than stacking on top of it.
                onClose();
                setCourseHoleMapOpen(true);
              }}
            >
              <Text style={styles.analysisText}>{t('hole.holeViewAnalysis')}</Text>
            </Pressable>
          ) : null}

          {canReanalyze ? (
            <Pressable
              accessibilityRole="button"
              testID="hole-view-reanalyze"
              disabled={reanalyzeState !== 'idle'}
              style={[styles.analysisButton, reanalyzeState !== 'idle' && styles.analysisButtonBusy]}
              onPress={() => void submitReanalyze()}
            >
              <Text style={styles.analysisText}>
                {t(
                  reanalyzeState === 'sending'
                    ? 'hole.holeViewReanalyzeRequesting'
                    : reanalyzeState === 'queued'
                      ? 'hole.holeViewReanalyzeQueued'
                      : reanalyzeState === 'unavailable'
                        ? 'hole.holeViewReanalyzeUnavailable'
                        : 'hole.holeViewReanalyze'
                )}
              </Text>
            </Pressable>
          ) : null}

          <Pressable
            accessibilityRole="button"
            testID="hole-view-close"
            style={styles.closeButton}
            onPress={onClose}
          >
            <Text style={styles.closeText}>{t('hole.close')}</Text>
          </Pressable>
        </View>

        {/* Over the whole screen rather than inside the map: the flyover is the picture
            while it plays, and the card's canvas is the smallest box on the screen. */}
        {flyoverPlaying && flyoverUrl ? (
          <View style={styles.flyoverOverlay} testID="hole-view-flyover-overlay">
            <Video
              testID="hole-view-flyover-video"
              ref={flyoverVideoRef}
              source={{ uri: flyoverUrl }}
              style={[styles.flyoverVideo, flyoverBox]}
              // The box is what sizes the picture; without this the <video> keeps its own
              // pixel size on web and the box merely crops it — a 720 x 1200 render inside
              // a phone-sized box showed nothing but the sky above the tee.
              videoStyle={styles.flyoverVideoSurface}
              resizeMode={ResizeMode.CONTAIN}
              shouldPlay={!flyoverPaused}
              // The default half-second between status frames is visible as a stepping
              // progress bar; this is the rate the fill moves at.
              progressUpdateIntervalMillis={100}
              onPlaybackStatusUpdate={flyoverScrub.applyStatus}
              onReadyForDisplay={(event) => {
                const ratio = readVideoAspectRatio(event);
                if (ratio) setMeasuredFlyoverAspectRatio(ratio);
              }}
            />
            {/* Goffy's own bar rather than `useNativeControls`: the platform ones hide
                themselves, and on web they are a second, differently styled seek bar. */}
            <View style={[styles.flyoverControls, { width: flyoverBox.width }]}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={flyoverPaused ? t('hole.flyoverPlay') : t('hole.flyoverPause')}
                testID="hole-view-flyover-play-pause"
                style={styles.flyoverPlayButton}
                onPress={() => setFlyoverPaused((paused) => !paused)}
              >
                <Ionicons
                  name={flyoverPaused ? 'play' : 'pause'}
                  size={18}
                  color={appColors.text.primary}
                />
              </Pressable>
              <VideoScrubBar
                scrub={flyoverScrub}
                accessibilityLabel={t('hole.flyoverPosition')}
                testIDPrefix="hole-view-flyover"
                // Beside the play button, so the bar has to claim the rest of the row itself.
                style={styles.flyoverScrubBar}
                thumbLabel={
                  flyoverDistance
                    ? formatHoleDistance(flyoverDistance.meters, distanceUnit)
                    : null
                }
                // The two inks the video paints its own numbers in: yellow counting up from
                // the tee, white counting down to the green.
                thumbLabelColor={
                  flyoverDistance?.measuredFrom === 'tee' ? FLYOVER_TEE_DISTANCE_INK : appColors.text.onMedia
                }
              />
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('hole.close')}
              testID="hole-view-flyover-close"
              style={styles.flyoverCloseButton}
              onPress={() => setFlyoverPlaying(false)}
            >
              <Ionicons name="close" size={20} color={appColors.text.primary} />
            </Pressable>
          </View>
        ) : null}
      </View>
    </Modal>
    {isAdmin && courseId ? (
      <CourseHoleMapModal
        visible={courseHoleMapOpen}
        onClose={() => setCourseHoleMapOpen(false)}
        apiBaseUrl={apiBaseUrl}
        token={token}
        courseId={courseId}
        holeNumbers={courseHoleMapHoleNumbers}
        initialHole={holeNumber}
        editable={isAdmin && Platform.OS === 'web'}
      />
    ) : null}
    </>
  );
}

/**
 * One imagery tile, served from the on-device cache once it is there.
 *
 * It renders from the network URL immediately and swaps to the local copy when the cache
 * resolves, so a cold cache costs nothing in time — the tile is never waiting on the disk
 * lookup before it starts loading.
 */
function Tile({
  url,
  testID,
  style,
}: {
  url: string;
  testID: string;
  style: StyleProp<ImageStyle>;
}) {
  const [uri, setUri] = useState(url);

  useEffect(() => {
    let cancelled = false;
    setUri(url);
    void getTileUri(url).then((resolved) => {
      if (!cancelled && resolved !== url) setUri(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return <Image testID={testID} source={{ uri }} style={style} />;
}

/** A line between two projected points, drawn as a rotated bar so no drawing library is needed. */
function HoleLine({
  from,
  to,
  variant = 'hole',
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  variant?: 'hole' | 'toGreen' | 'targetToGreen';
}) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (!(length > 0)) return null;
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  return (
    <View
      testID={
        variant === 'toGreen'
          ? 'hole-view-player-line'
          : variant === 'targetToGreen'
            ? 'hole-view-target-line'
            : 'hole-view-line'
      }
      pointerEvents="none"
      style={[
        styles.holeLine,
        variant === 'toGreen' && styles.playerLine,
        variant === 'targetToGreen' && styles.targetLine,
        {
          left: (from.x + to.x) / 2 - length / 2,
          top: (from.y + to.y) / 2 - 1,
          width: length,
          transform: [{ rotate: `${angle}deg` }],
        },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.background.app, 0.88),
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
  },
  card: {
    width: '100%',
    maxWidth: 560,
    flex: 1,
    maxHeight: 720,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.base,
    padding: 12,
    gap: 10,
  },
  header: {
    gap: 2,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 12,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    color: appColors.text.heading,
    fontSize: 18,
    fontWeight: '800',
  },
  betaPill: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.accent.goldBorder,
    backgroundColor: appColors.accent.goldSoft,
  },
  betaPillText: {
    color: appColors.accent.gold,
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  holeStepButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.alt,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  holeStepButtonText: {
    color: appColors.text.heading,
    fontSize: 18,
    fontWeight: '800',
    lineHeight: 20,
  },
  betaNotice: {
    color: appColors.text.muted,
    fontSize: 11,
    lineHeight: 15,
  },
  // The row's own `alignItems: 'center'` (see `metaRow`) handles vertical alignment for both
  // of its children now, so neither needs an `alignSelf` of its own the way it did stacked.
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  disclaimerToggle: {
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  disclaimerToggleText: {
    color: appColors.text.muted,
    fontSize: 11,
    fontWeight: '600',
  },
  readout: {
    alignItems: 'flex-end',
  },
  readoutValue: {
    color: appColors.accent.primary,
    fontSize: 30,
    fontWeight: '800',
    // Digits that change every second must not shuffle the label under them.
    fontVariant: ['tabular-nums'],
    lineHeight: 32,
  },
  readoutLabel: {
    color: appColors.text.muted,
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  subtitle: {
    color: appColors.text.muted,
    fontSize: 13,
  },
  canvas: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.app,
    overflow: 'hidden',
  },
  tile: {
    position: 'absolute',
  },
  holeLine: {
    position: 'absolute',
    height: 2,
    backgroundColor: alpha(appColors.text.primary, 0.75),
  },
  // The live line to the green is the one the player is reading a number off, so it is the
  // brighter of the two.
  playerLine: {
    height: 3,
    backgroundColor: appColors.accent.primary,
  },
  // The second leg is the shot that is left, not the one being planned, so it reads quieter.
  targetLine: {
    height: 2,
    backgroundColor: appColors.accent.gold,
    opacity: 0.85,
  },
  targetMarker: {
    position: 'absolute',
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 2,
    borderColor: appColors.accent.gold,
    backgroundColor: alpha(appColors.accent.gold, 0.18),
    alignItems: 'center',
    justifyContent: 'center',
  },
  targetCrosshairVertical: {
    position: 'absolute',
    width: 2,
    height: 26,
    backgroundColor: appColors.accent.gold,
  },
  targetCrosshairHorizontal: {
    position: 'absolute',
    width: 26,
    height: 2,
    backgroundColor: appColors.accent.gold,
  },
  // Tighter than `card`'s own 10 px gap — see the comment where this is used. The rows inside
  // are read at a glance, not read line by line, so the wider gap between them and the map
  // (or the map and its header) would just be repeated space nobody needed.
  infoStack: {
    gap: 4,
  },
  footerSlot: {
    height: 58,
    justifyContent: 'center',
  },
  targetPanel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.goldBorder,
    backgroundColor: appColors.accent.goldSoft,
  },
  targetLeg: {
    flex: 1,
  },
  targetLegValue: {
    color: appColors.accent.gold,
    fontSize: 20,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
  },
  targetLegLabel: {
    color: appColors.text.muted,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  targetLegDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: appColors.accent.goldBorder,
  },
  targetClear: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  targetClearText: {
    color: appColors.text.secondary,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  zoomControls: {
    position: 'absolute',
    right: 8,
    top: 8,
    alignItems: 'flex-end',
    gap: 6,
  },
  zoomButton: {
    minWidth: 34,
    height: 34,
    paddingHorizontal: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: alpha(appColors.text.primary, 0.3),
    backgroundColor: alpha(appColors.background.app, 0.62),
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  zoomButtonText: {
    color: appColors.text.primary,
    fontSize: 20,
    fontWeight: '800',
    lineHeight: 22,
  },
  zoomFitText: {
    color: appColors.text.secondary,
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  flyoverButton: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    width: 34,
    height: 34,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: alpha(appColors.text.primary, 0.3),
    backgroundColor: alpha(appColors.background.app, 0.62),
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  flyoverOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(appColors.neutral.black, 0.94),
  },
  flyoverVideo: {
    backgroundColor: appColors.neutral.black,
  },
  /** Fills the box above; see the `videoStyle` comment at the call site. */
  flyoverVideoSurface: {
    width: '100%',
    height: '100%',
  },
  /** As wide as the picture, so the bar's ends line up with the video's. */
  flyoverControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingTop: 10,
  },
  flyoverScrubBar: { flex: 1 },
  flyoverPlayButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: alpha(appColors.background.app, 0.72),
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  flyoverCloseButton: {
    position: 'absolute',
    right: 16,
    top: 16,
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: alpha(appColors.background.app, 0.72),
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  basemapControls: {
    position: 'absolute',
    left: 8,
    bottom: 8,
    flexDirection: 'row',
    gap: 4,
    padding: 3,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: alpha(appColors.text.primary, 0.25),
    backgroundColor: alpha(appColors.background.app, 0.62),
  },
  basemapButton: {
    paddingVertical: 5,
    paddingHorizontal: 9,
    borderRadius: 7,
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  basemapButtonActive: {
    backgroundColor: appColors.accent.primary,
  },
  basemapText: {
    color: appColors.text.secondary,
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  // Against the filled pill, not the map: the dark ink is what makes the choice read as made.
  basemapTextActive: {
    color: appColors.text.inverse,
  },
  windBadge: {
    position: 'absolute',
    left: 8,
    top: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: alpha(appColors.text.primary, 0.25),
    backgroundColor: alpha(appColors.background.app, 0.62),
  },
  // An arrow built from two blocks: a triangle head over a stem, rotated as one.
  windArrow: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  windArrowHead: {
    position: 'absolute',
    top: 1,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderBottomWidth: 9,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: appColors.accent.freeze,
    backgroundColor: 'transparent',
  },
  windArrowTail: {
    position: 'absolute',
    bottom: 2,
    width: 3,
    height: 11,
    borderRadius: 1,
    backgroundColor: appColors.accent.freeze,
  },
  windSpeed: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
  },
  windDescriptor: {
    color: appColors.text.muted,
    fontSize: 10,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  legendHint: {
    color: appColors.text.muted,
    fontSize: 11,
    flexShrink: 1,
  },
  accuracyRing: {
    position: 'absolute',
    borderWidth: 1,
    borderColor: alpha(appColors.chart.sky, 0.7),
    backgroundColor: alpha(appColors.chart.sky, 0.18),
  },
  // A wedge spreading out from the player in the direction the phone is pointed. Deliberately
  // soft-edged and translucent: the compass is a hint about aim, not a measurement.
  headingCone: {
    position: 'absolute',
    width: HEADING_CONE_RADIUS * 2,
    height: HEADING_CONE_RADIUS * 2,
    alignItems: 'center',
  },
  headingConeWedge: {
    position: 'absolute',
    top: 0,
    width: 0,
    height: 0,
    // Border trick: a downward triangle whose apex lands on the container centre, which is
    // the player's own position.
    borderLeftWidth: 13,
    borderRightWidth: 13,
    borderTopWidth: HEADING_CONE_RADIUS,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: alpha(appColors.accent.blue, 0.45),
    backgroundColor: 'transparent',
  },
  playerMarker: {
    position: 'absolute',
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 3,
    borderColor: appColors.text.primary,
    backgroundColor: appColors.accent.blue,
  },
  teeMarkerGroup: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  teeMarker: {
    width: 14,
    height: 14,
    borderRadius: 3,
    borderWidth: 2,
    borderColor: appColors.text.primary,
    backgroundColor: alpha(appColors.background.app, 0.6),
  },
  teeLabel: {
    color: appColors.text.primary,
    fontSize: 10,
    fontWeight: '700',
    backgroundColor: alpha(appColors.background.app, 0.55),
    paddingHorizontal: 4,
    borderRadius: 3,
  },
  hazardMarker: {
    position: 'absolute',
    // The anchor point is already the shape's right edge plus a gap, so the chip's own left
    // edge starts right there — only the vertical centring is approximate, since the chip's
    // height barely varies with digit count the way its width does.
    marginTop: -7,
  },
  /**
   * A bend in the playing line: a ring the finger can find, hollow so the imagery under it
   * stays readable — the same shape the hole map draws a turning point with, so a bend looks
   * like a bend on both screens.
   */
  turnMarker: {
    position: 'absolute',
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  turnMarkerRing: {
    position: 'absolute',
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: appColors.text.onMedia,
  },
  turnMarkerDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: appColors.text.onMedia,
  },
  /** A leg's distance, centred on the span it measures. Half its width is taken off the left. */
  legDistance: {
    position: 'absolute',
    width: 52,
    alignItems: 'center',
  },
  legDistanceText: {
    color: appColors.text.primary,
    fontSize: 11,
    fontWeight: '700',
    backgroundColor: alpha(appColors.background.app, 0.68),
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 3,
    overflow: 'hidden',
  },
  greenMarker: {
    position: 'absolute',
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  greenMarkerRing: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 22,
    borderWidth: 3,
    borderColor: appColors.accent.primary,
  },
  greenMarkerDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: appColors.accent.primary,
  },
  attribution: {
    position: 'absolute',
    right: 6,
    bottom: 4,
    color: alpha(appColors.text.primary, 0.75),
    fontSize: 9,
    // A hairline backing keeps it legible over bright fairway imagery.
    backgroundColor: alpha(appColors.background.app, 0.45),
    paddingHorizontal: 4,
    borderRadius: 3,
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(appColors.background.app, 0.72),
    padding: OVERLAY_PADDING,
  },
  overlayText: {
    color: appColors.text.secondary,
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  legend: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendTee: {
    width: 12,
    height: 12,
    borderRadius: 3,
    borderWidth: 2,
    borderColor: appColors.text.primary,
  },
  legendGreen: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: appColors.accent.primary,
  },
  legendPlayer: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: appColors.text.primary,
    backgroundColor: appColors.accent.blue,
  },
  legendLabel: {
    color: appColors.text.muted,
    fontSize: 12,
  },
  legendCheckbox: {
    width: 14,
    height: 14,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: appColors.text.secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  legendCheckboxChecked: {
    backgroundColor: appColors.accent.primary,
    borderColor: appColors.accent.primary,
  },
  legendCheckboxMark: {
    color: appColors.text.inverse,
    fontSize: 10,
    fontWeight: '900',
    lineHeight: 11,
  },
  closeButton: {
    alignSelf: 'stretch',
    height: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.alt,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' }, default: {} }),
  },
  closeText: {
    color: appColors.text.secondary,
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
  },
  reportLink: {
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  reportLinkText: {
    color: appColors.text.secondary,
    fontSize: 12,
    textDecorationLine: 'underline',
  },
  reportLinkDone: {
    color: appColors.accent.primary,
    textDecorationLine: 'none',
  },
  analysisButton: {
    alignSelf: 'stretch',
    height: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.surface.alt,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  analysisText: {
    color: appColors.accent.primary,
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
  },
  // Sending, queued or unavailable are all non-idle: the button has nothing left to do until
  // this view is reopened, which the dimmed look says before a press finds out the hard way.
  analysisButtonBusy: { opacity: 0.6 },
});

export default HoleViewModal;
